// ModalComponent.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import styled from "styled-components";
import { gql } from "@apollo/client";
import { client } from "../../../client";
import { uploadFileWithPresignedUrl } from "../../../utils/s3Uploader";
import FileUploader from "./FileUploader";

const ModalContainer = styled(Box)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 600px;
  height: 90vh;
  background-color: white;
  padding: 24px;
  z-index: 100;
  border: 1px solid #ccc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
`;

const FormRow = styled(Box)`
  margin-bottom: 16px;
`;

const FilePreviewContainer = styled(Box)`
  margin-top: 8px;
`;

const ModifyDocument = ({
  isOpen,
  data,
  handleCloseClick,
  refetchParentData,
}) => {
  const [editableData, setEditableData] = useState({});
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [selectedHwpFile, setSelectedHwpFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      const pdfDoc = data.documentUrlList?.find((doc) => doc.type === "PDF");
      const hwpDoc = data.documentUrlList?.find((doc) => doc.type === "HWP");
      setEditableData({
        ...data,
        pdfUrl: pdfDoc?.object_url || null,
        hwpUrl: hwpDoc?.object_url || null,
        is_old: !!data.is_old,
      });
    } else {
      setEditableData({});
    }
  }, [data]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (event) => {
    setEditableData((prev) => ({ ...prev, is_old: event.target.checked }));
  };

  const handleRemoveFile = (fileType) => {
    if (window.confirm(`${fileType} 파일을 제거하시겠습니까?`)) {
      if (fileType === "PDF") {
        setEditableData((prev) => ({ ...prev, pdfUrl: null }));
        setSelectedPdfFile(null);
      } else if (fileType === "HWP") {
        setEditableData((prev) => ({ ...prev, hwpUrl: null }));
        setSelectedHwpFile(null);
      }
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const [pdfUrl, hwpUrl] = await Promise.all([
        selectedPdfFile
          ? uploadFileWithPresignedUrl(client, selectedPdfFile)
          : Promise.resolve(editableData.pdfUrl),
        selectedHwpFile
          ? uploadFileWithPresignedUrl(client, selectedHwpFile)
          : Promise.resolve(editableData.hwpUrl),
      ]);

      const document_url_list = [];
      if (pdfUrl) document_url_list.push({ type: "PDF", object_url: pdfUrl });
      if (hwpUrl) document_url_list.push({ type: "HWP", object_url: hwpUrl });

      const MODIFY_DOCUMENT = gql`
        mutation modifyDocument(
          $id: Int!
          $order: Int
          $docu_type1_id: Int
          $docu_type2_id: Int
          $name: String
          $price: String
          $grade: String
          $year: Int
          $month_id: Int
          $subject: String
          $publisher_id: Int
          $category1_id: Int
          $category2_id: Int
          $document_url_list: [document_url_input_v2]
          $is_old: Boolean
        ) {
          modifyDocument(
            id: $id
            order: $order
            docu_type1_id: $docu_type1_id
            docu_type2_id: $docu_type2_id
            name: $name
            price: $price
            grade: $grade
            year: $year
            month_id: $month_id
            subject: $subject
            publisher_id: $publisher_id
            category1_id: $category1_id
            category2_id: $category2_id
            document_url_list: $document_url_list
            is_old: $is_old
          ) {
            status
          }
        }
      `;

      const variables = {
        id: data.id,
        order: editableData.order ? parseInt(editableData.order, 10) : null,
        docu_type1_id: editableData.docu_type1_id
          ? parseInt(editableData.docu_type1_id, 10)
          : null,
        docu_type2_id: editableData.docu_type2_id
          ? parseInt(editableData.docu_type2_id, 10)
          : null,
        name: editableData.name,
        price: editableData.price ? String(editableData.price) : null,
        grade: editableData.grade ? String(editableData.grade) : null,
        year: editableData.year ? parseInt(editableData.year, 10) : null,
        month_id: editableData.month_id
          ? parseInt(editableData.month_id, 10)
          : null,
        subject: editableData.subject,
        publisher_id: editableData.publisher_id
          ? parseInt(editableData.publisher_id, 10)
          : null,
        category1_id: editableData.category1_id
          ? parseInt(editableData.category1_id, 10)
          : null,
        category2_id: editableData.category2_id
          ? parseInt(editableData.category2_id, 10)
          : null,
        document_url_list,
        is_old: Boolean(editableData.is_old),
      };

      await client.mutate({
        mutation: MODIFY_DOCUMENT,
        variables: variables,
        fetchPolicy: "no-cache",
      });

      alert("성공적으로 수정되었습니다.");
      refetchParentData();
      handleCloseClick();
    } catch (err) {
      alert(`수정 중 오류가 발생했습니다: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onClose={handleCloseClick}>
      <ModalContainer>
        <h2>자료 정보 수정</h2>
        <form>
          <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
            <InputLabel id="docu_type1_id-label">대분류</InputLabel>
            <Select
              labelId="docu_type1_id-label"
              id="docu_type1_id"
              name="docu_type1_id"
              value={editableData.docu_type1_id}
              defaultValue={data.docu_type1_id}
              label="Document Type 1"
              onChange={handleInputChange}
              disabled
            >
              <MenuItem value={1}>모의고사/학평</MenuItem>
              <MenuItem value={2}>고등 부교재 (EBS 교재)</MenuItem>
              <MenuItem value={3}>수능연계교재 (수능특강, 수능완성)</MenuItem>
              <MenuItem value={4}>고등교과서</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
            <InputLabel id="docu_type2_id-label">중분류</InputLabel>
            <Select
              labelId="docu_type2_id-label"
              id="docu_type2_id"
              name="docu_type2_id"
              value={editableData.docu_type2_id}
              defaultValue={data.docu_type2_id}
              label="Document Type 2"
              onChange={handleInputChange}
            >
              <MenuItem value={1}>수업용자료</MenuItem>
              <MenuItem value={2}>워크북</MenuItem>
              <MenuItem value={3}>변형문제</MenuItem>
              <MenuItem value={4}>패키지</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
            <TextField
              label="자료명"
              name="name"
              value={editableData.name}
              onChange={handleInputChange}
            />
          </FormControl>

          <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
            <TextField
              label="가격"
              name="price"
              value={editableData.price}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
            <TextField
              label="순서"
              name="order"
              value={editableData.order}
              onChange={handleInputChange}
            />
          </FormControl>

          <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editableData.is_old}
                  onChange={handleSwitchChange}
                  name="isOld"
                />
              }
              label="구버전 교재자료"
            />
          </FormControl>

          {editableData.docu_type1_id === 1 ? (
            <div>
              <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
                <TextField
                  label="학년"
                  name="grade"
                  value={editableData.grade}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
                <TextField
                  label="시행 연도"
                  name="year"
                  value={editableData.year}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl fullWidth sx={{ marginBottom: 1, width: "500px" }}>
                <TextField
                  label="시행 월"
                  name="month_id"
                  value={editableData.month_id}
                  onChange={handleInputChange}
                />
              </FormControl>
            </div>
          ) : null}

          {editableData.docu_type1_id == "2" ? (
            <div>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel id="docu_type2_id-label">분류 1</InputLabel>
                <Select
                  labelId="docu_type2_id-label"
                  id="category1_id"
                  name="category1_id"
                  value={editableData.category1_id}
                  defaultValue={1}
                  label="분류 1"
                  onChange={handleInputChange}
                >
                  <MenuItem value={1}>리딩파워</MenuItem>
                  <MenuItem value={2}>올림포스</MenuItem>
                  <MenuItem value={3}>수능특강 LIGHT</MenuItem>
                  <MenuItem value={4}>
                    올림포스 전국연합 기출 영어 독해
                  </MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel id="docu_type2_id-label">분류 2</InputLabel>
                {editableData.category1_id == 1 ? (
                  <Select
                    labelId="docu_type2_id-label"
                    id="docu_type2category2_id_id"
                    name="category2_id"
                    value={editableData.category2_id}
                    defaultValue={1}
                    label="분류 2"
                    onChange={handleInputChange}
                  >
                    <MenuItem value={1}>리딩파워 유형편 기본</MenuItem>
                    <MenuItem value={2}>리딩파워 유형편 완성</MenuItem>
                    <MenuItem value={3}>리딩파워 주제별독해완성</MenuItem>
                  </Select>
                ) : editableData.category1_id == 2 ? (
                  <Select
                    labelId="docu_type2_id-label"
                    id="docu_type2category2_id_id"
                    name="category2_id"
                    value={editableData.category2_id}
                    defaultValue={1}
                    label="분류 2"
                    onChange={handleInputChange}
                  >
                    <MenuItem value={4}>올림포스1</MenuItem>
                    <MenuItem value={5}>올림포스2</MenuItem>
                  </Select>
                ) : editableData.category1_id == 3 ? (
                  <Select
                    labelId="docu_type2_id-label"
                    id="docu_type2category2_id_id"
                    name="category2_id"
                    value={editableData.category2_id}
                    defaultValue={1}
                    label="분류 2"
                    onChange={handleInputChange}
                  >
                    <MenuItem value={6}>수능특강 LIGHT 영어편</MenuItem>
                    <MenuItem value={7}>수능특강 LIGHT 영어독해편</MenuItem>
                  </Select>
                ) : editableData.category1_id == 4 ? (
                  <Select
                    labelId="docu_type2_id-label"
                    id="docu_type2category2_id_id"
                    name="category2_id"
                    value={editableData.category2_id}
                    defaultValue={1}
                    label="분류 2"
                    onChange={handleInputChange}
                  >
                    <MenuItem value={8}>고1</MenuItem>
                    <MenuItem value={9}>고2</MenuItem>
                  </Select>
                ) : null}
              </FormControl>
            </div>
          ) : null}

          {editableData.docu_type1_id == "3" ? (
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <InputLabel id="docu_type2_id-label">분류 1</InputLabel>
              <Select
                labelId="docu_type2_id-label"
                id="category1_id"
                name="category1_id"
                value={editableData.category1_id}
                defaultValue={1}
                label="분류 1"
                onChange={handleInputChange}
              >
                <MenuItem value={5}>수능특강 영어</MenuItem>
                <MenuItem value={6}>수능특강 영어독해</MenuItem>
                <MenuItem value={7}>수능완성</MenuItem>
              </Select>
            </FormControl>
          ) : null}

          {editableData.docu_type1_id == "4" ? (
            <div>
              <TextField
                fullWidth
                label="과목"
                id="subject"
                name="subject"
                value={editableData.subject}
                onChange={handleInputChange}
                sx={{ marginBottom: 2 }}
              />

              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel id="docu_type2_id-label">출판사</InputLabel>
                <Select
                  labelId="docu_type2_id-label"
                  id="publisher_id"
                  name="publisher_id"
                  value={editableData.docu_type2_id}
                  defaultValue={1}
                  label="출판사"
                  onChange={handleInputChange}
                >
                  <MenuItem value={1}>교학사</MenuItem>
                  <MenuItem value={2}>진학사</MenuItem>
                </Select>
              </FormControl>
            </div>
          ) : null}

          <FormRow>
            <h4>PDF 파일</h4>
            {editableData.pdfUrl ? (
              <FilePreviewContainer>
                <a
                  href={editableData.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  현재 PDF 파일 보기
                </a>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRemoveFile("PDF")}
                  sx={{ ml: 2 }}
                >
                  PDF 파일 제거
                </Button>
              </FilePreviewContainer>
            ) : (
              <p>첨부된 PDF 파일이 없습니다.</p>
            )}
            <FileUploader
              selectedFiles={selectedPdfFile ? [selectedPdfFile] : []}
              setSelectedFiles={(files) => setSelectedPdfFile(files[0] || null)}
            />
          </FormRow>

          <FormRow>
            <h4>HWP 파일</h4>
            {editableData.hwpUrl ? (
              <FilePreviewContainer>
                <a
                  href={editableData.hwpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  현재 HWP 파일 보기
                </a>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRemoveFile("HWP")}
                  sx={{ ml: 2 }}
                >
                  HWP 파일 제거
                </Button>
              </FilePreviewContainer>
            ) : (
              <p>첨부된 HWP 파일이 없습니다.</p>
            )}
            <FileUploader
              selectedFiles={selectedHwpFile ? [selectedHwpFile] : []}
              setSelectedFiles={(files) => setSelectedHwpFile(files[0] || null)}
            />
          </FormRow>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={handleCloseClick} sx={{ mr: 1 }}>
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? "저장 중..." : "저장"}
            </Button>
          </Box>
        </form>
      </ModalContainer>
    </Modal>
  );
};

export default ModifyDocument;
